import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { EmptyState } from "./InventoryHome";
import RateHistoryPanel from "./RateHistoryPanel";
import { Search, Package, MapPin, Tag } from "lucide-react";

/** Items tab — list all inventory items + search + category filter +
 *  click row to see per-item distribution across sites. */
export default function ItemsTab({ items, categories, refreshTick }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [selected, setSelected] = useState(null); // item_id for distribution
  const [dist, setDist] = useState(null);
  const [distLoading, setDistLoading] = useState(false);
  const [panelMode, setPanelMode] = useState("distribution"); // 'distribution' | 'rates'
  const [ratesByItem, setRatesByItem] = useState({}); // item_id -> current rate (for the column)

  const filtered = useMemo(() => {
    let r = items;
    if (cat) r = r.filter((i) => i.category === cat);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      r = r.filter((i) => (i.name || "").toLowerCase().includes(s));
    }
    return r;
  }, [items, q, cat]);

  // Eagerly load distribution when selecting an item
  useEffect(() => {
    if (!selected) {
      setDist(null);
      return;
    }
    let cancel = false;
    setDistLoading(true);
    api
      .get(`/inventory/by-item/${selected}`)
      .then((r) => !cancel && setDist(r.data))
      .catch(() => !cancel && setDist({ sites: [], total_quantity: 0 }))
      .finally(() => !cancel && setDistLoading(false));
    return () => {
      cancel = true;
    };
  }, [selected, refreshTick]);

  // Load current rate for every visible item (one batched call per item; cached).
  useEffect(() => {
    let cancel = false;
    const todo = items
      .map((i) => i.item_id)
      .filter((id) => !(id in ratesByItem));
    if (todo.length === 0) return;
    (async () => {
      const updates = {};
      // Limit concurrency to avoid hammering — sequential is fine for ≤200 items
      for (const id of todo) {
        try {
          const r = await api.get(`/inventory/items/${id}/rates`);
          updates[id] = r.data?.current_rate ?? null;
        } catch {
          updates[id] = null;
        }
        if (cancel) return;
      }
      if (!cancel) setRatesByItem((prev) => ({ ...prev, ...updates }));
    })();
    return () => { cancel = true; };
  }, [items, refreshTick]);

  const selectedItem = items.find((i) => i.item_id === selected) || dist?.item;

  if (!items.length) {
    return (
      <EmptyState
        testid="items-empty"
        icon={Package}
        title="No items yet"
        body="Add your first inventory item from the toolbar above. Items are the catalogue — stock is recorded per site."
      />
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      {/* List + filters */}
      <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl">
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-2.5">
          <div className="flex-1 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="items-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search items…"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30"
            />
          </div>
          <select
            data-testid="items-cat"
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30"
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto" data-testid="items-list">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No items match the filters.</div>
          ) : (
            filtered.map((it) => {
              const curRate = ratesByItem[it.item_id];
              return (
              <div
                key={it.item_id}
                data-testid={`item-row-${it.item_id}`}
                className={`px-4 py-3 hover:bg-slate-50 transition-colors flex items-center gap-3 ${
                  selected === it.item_id ? "bg-olive/5" : ""
                }`}
              >
                <button
                  onClick={() => { setSelected(it.item_id); setPanelMode("distribution"); }}
                  className="flex-1 text-left flex items-center gap-3 min-w-0"
                >
                  <div className="w-9 h-9 rounded-xl bg-slate-100 grid place-items-center text-slate-500 shrink-0">
                    <Package size={14} strokeWidth={1.8} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-slate-900 truncate">{it.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {it.category} · {it.unit}
                    </div>
                  </div>
                </button>
                <button
                  data-testid={`item-rate-btn-${it.item_id}`}
                  onClick={() => { setSelected(it.item_id); setPanelMode("rates"); }}
                  className={"shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors " +
                    (curRate != null
                      ? "bg-olive/10 border-olive/30 text-olive hover:bg-olive/20"
                      : "bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100")}
                  title="View rate history"
                >
                  <Tag size={11} strokeWidth={2.2} className="inline mr-1" />
                  {curRate != null ? `₹${Number(curRate).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "Set rate"}
                </button>
              </div>
              );
            })
          )}
        </div>
      </div>

      {/* Right panel — distribution OR rate history (driven by panelMode) */}
      <div data-testid="item-distribution">
        {!selected ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 text-center py-8">
            <MapPin className="mx-auto text-slate-300" size={28} strokeWidth={1.5} />
            <div className="text-sm font-medium text-slate-900 mt-2">Pick an item</div>
            <p className="text-xs text-slate-500 mt-1">See where the item is held across sites, its grand total, or open its rate history.</p>
          </div>
        ) : panelMode === "rates" ? (
          <RateHistoryPanel
            open={true}
            item={selectedItem}
            onClose={() => setPanelMode("distribution")}
            onChanged={() => setRatesByItem((p) => ({ ...p, [selected]: undefined }))}
          />
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Item distribution</div>
              <button
                data-testid="panel-open-rates"
                onClick={() => setPanelMode("rates")}
                className="rounded-full px-2.5 py-1 text-[10px] font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"
              >
                <Tag size={10} strokeWidth={2.2} /> Rates
              </button>
            </div>
            {distLoading ? (
              <div className="text-sm text-slate-500 text-center py-8">Loading distribution…</div>
            ) : !dist ? null : (
              <>
                <div className="text-lg font-semibold mt-1 text-slate-900 truncate">{dist.item?.name}</div>
                <div className="text-xs text-slate-500">{dist.item?.category} · {dist.item?.unit}</div>

                <div className="mt-4 rounded-xl bg-olive/10 border border-olive/30 px-4 py-3 flex items-baseline justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-olive">Total in network</span>
                  <span className="text-2xl font-semibold tracking-tight text-olive" data-testid="item-total-qty">
                    {dist.total_quantity}
                  </span>
                </div>

                <div className="mt-4 space-y-2" data-testid="item-sites-list">
                  {(dist.sites || []).length === 0 ? (
                    <div className="text-xs text-slate-500 text-center py-3">No stock recorded for this item yet.</div>
                  ) : (
                    dist.sites.map((s) => (
                      <div
                        key={s.site}
                        data-testid={`site-row-${s.site}`}
                        className="flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2 hover:bg-slate-50 transition-colors"
                      >
                        <div className="text-sm text-slate-800 truncate">{s.site}</div>
                        <div className="text-sm font-semibold text-slate-900">{s.quantity}</div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
